import { fabric } from 'fabric';
import { TextOptions } from 'fabric/fabric-impl';
import { uniqueId } from 'lodash';
import Vec2 from 'vec2';

import {
    LABEL_OFFSET,
    LABEL_OFFSET_CORNER_SIGN,
    OFFSET_MEASUREMENT,
    OFFSET_MEASUREMENT_TEXT,
    enforceEdgeConstraints,
    enforceRadiusConstraints,
    getCornerLabelPosition,
    getDirection,
    getOutlinePointsFromShape,
} from '@canvas-common';
import {
    BLACK,
    CANVAS_OBJECT_TYPE_PREFIX,
    DEFAULT_CORNER_RADIUS,
    DRAWING_SHAPE_COLOR_PALETTE,
    FREE_CORNER_RADIUS,
    INCH_QUOTE,
    KEYS,
    MEASUREMENT_LABEL_MAX_LENGTH,
    TEN_THOUSAND,
} from '@common/constants';
import { LineDirection } from '@common/enums';
import {
    ArrowInfo,
    CornerInfo,
    CornerRadiusModel,
    DrawingShape,
    LineSegment,
    MeasureInfo,
    getMaxFromRange,
    getMinFromRange,
} from '@common/models';

import variables from './variables.module.scss';

export class EditableLabel {
    parentShape: DrawingShape;
    labelText: fabric.Text;
    measurementText: fabric.IText;

    constructor(
        parentShape: DrawingShape,
        _canvas: fabric.Canvas,
        interiorColor: string,
        label: string,
        Info: MeasureInfo
    ) {
        this.parentShape = parentShape;
        this.labelText = EditableLabel.newLabelText(label);
        this.measurementText = EditableLabel.newMeasureText(`${Info.size}${INCH_QUOTE}`, interiorColor);

        this.measurementText.on('mousedown', () => {
            if (this.measurementText.name) {
                // ignore orphaned labels
                const hasEditableLabels = this.parentShape.editableLabels.some(
                    (editableLabel) => editableLabel.measurementText.name === this.measurementText.name
                );

                if (hasEditableLabels) {
                    this.measurementText.enterEditing();
                } else {
                    // TODO orphaned label not removable, so moving way off to right as a workaround
                    // Its no longer an object on the canvas yet it shows until next refresh
                    this.measurementText.left = TEN_THOUSAND;
                }
            }
        });

        this.labelText.on('mousedown', (options) => {
            // if a user clicks the label it should enter editing mode
            this.measurementText.fire('mousedown', options);
            this.measurementText.selectAll();
        });

        this.measurementText.on('editing:entered', () => {
            this.measurementText.hiddenTextarea?.setAttribute('maxlength', MEASUREMENT_LABEL_MAX_LENGTH.toString());
            this.measurementText.selectAll();
            this.measurementText.updateFromTextArea();
        });

        this.measurementText.onKeyDown = (e: KeyboardEvent) => {
            if (e.key === KEYS.ENTER || e.key === KEYS.TAB) {
                this.measurementText.exitEditing();
            }
        };
    }

    public static newLabelText = (label: string | undefined, options?: Partial<TextOptions>): fabric.Text => {
        return new fabric.Text(label ?? '', {
            stroke: DRAWING_SHAPE_COLOR_PALETTE.NORMAL.STROKE,
            fill: DRAWING_SHAPE_COLOR_PALETTE.NORMAL.STROKE,
            strokeWidth: 0.1,
            fontSize: 3,
            fontFamily: variables.fontFamily,
            name: `${CANVAS_OBJECT_TYPE_PREFIX.SHAPE_MEASURE_LABEL}_${uniqueId()}`,
            hoverCursor: 'pointer',
            hasControls: false,
            lockMovementX: true,
            lockMovementY: true,
            selectable: false,
            data: {
                highlightable: true,
                inInterior: true,
            },
            ...options,
        });
    };

    public static newMeasureText = (
        label: string | undefined,
        interiorColor: string | fabric.Pattern | fabric.Gradient | undefined,
        options?: Partial<TextOptions>
    ): fabric.IText => {
        return new fabric.IText(label ?? '', {
            fontFamily: variables.fontFamily,
            stroke: DRAWING_SHAPE_COLOR_PALETTE.NORMAL.STROKE,
            fill: interiorColor,
            strokeWidth: 0.1,
            editable: true,
            fontSize: 3,
            selectable: true,
            hoverCursor: 'pointer',
            hasControls: false,
            lockMovementX: true,
            lockMovementY: true,
            name: `${CANVAS_OBJECT_TYPE_PREFIX.SHAPE_MEASURE_LABEL}_${uniqueId()}`,
            data: {
                highlightable: true,
                inInterior: true,
            },
            ...options,
        });
    };

    toJSON() {
        return {
            parentShape: this.parentShape.id,
            labelText: this.labelText.text,
            measurementText: this.measurementText.text,
        };
    }

    hide(): void {
        this.measurementText.canvas?.remove(this.measurementText);
        this.labelText.canvas?.remove(this.labelText);
    }

    darken(): void {
        this.labelText.set({
            stroke: DRAWING_SHAPE_COLOR_PALETTE.ACTIVE.STROKE,
            fill: DRAWING_SHAPE_COLOR_PALETTE.ACTIVE.STROKE,
        });

        this.measurementText.set({
            stroke: DRAWING_SHAPE_COLOR_PALETTE.ACTIVE.STROKE,
            fill: DRAWING_SHAPE_COLOR_PALETTE.ACTIVE.STROKE,
        });
    }

    lighten(): void {
        this.measurementText.set({
            stroke: DRAWING_SHAPE_COLOR_PALETTE.NORMAL.STROKE,
            fill: DRAWING_SHAPE_COLOR_PALETTE.NORMAL.STROKE,
        });

        this.labelText.set({
            stroke: DRAWING_SHAPE_COLOR_PALETTE.NORMAL.STROKE,
            fill: DRAWING_SHAPE_COLOR_PALETTE.NORMAL.STROKE,
        });
    }
}

export class EditableLengthLabel extends EditableLabel {
    isInterior = false;

    constructor(
        parentShape: DrawingShape,
        lineSegment: LineSegment,
        rootArrowInfo: ArrowInfo,
        label: string,
        onEdit: (editedArrow: ArrowInfo, shape: DrawingShape, enteredValue?: number) => void,
        canvas: fabric.Canvas,
        interiorColor = BLACK,
        activeDrawingShape: DrawingShape
    ) {
        super(parentShape, canvas, interiorColor, label, rootArrowInfo);
        if (!rootArrowInfo.size) {
            console.error('Tried to init editableLabel with undefined length', rootArrowInfo);
        }

        if (rootArrowInfo.isInterior) {
            this.measurementText.stroke = interiorColor;
            this.measurementText.fill = interiorColor;
            this.isInterior = true;
        }

        this.measurementText.on('editing:exited', () => {
            let arrowSize = 0;
            const updateArrow = (arrow: ArrowInfo): ArrowInfo => {
                const edge = parentShape.drawingEdges.find((edge) => edge.id === arrow.drawingEdgeId);
                const edgeType = edge?.sideItem.type;

                if (this.measurementText.text) {
                    arrowSize = parseFloat(this.measurementText.text);
                    const length = enforceEdgeConstraints({
                        arrow,
                        value: arrowSize,
                        edgeType,
                        shape: parentShape,
                    });
                    return { ...arrow, size: length };
                } else {
                    // handling the case where the user enters an empty string
                    // or is otherwise undefined.
                    return { ...arrow };
                }
            };

            const newArrow = updateArrow(rootArrowInfo);
            onEdit(newArrow, parentShape, arrowSize);
        });

        const isParentShapeNameSameAsActiveDrawing =
            parentShape.canvasObjectName === activeDrawingShape?.canvasObjectName;

        const pos = this.getMeasurementPosition(lineSegment, isParentShapeNameSameAsActiveDrawing);

        this.measurementText.left = pos.x;
        this.measurementText.top = pos.y;
        this.labelText.left = pos.x - LABEL_OFFSET;
        this.labelText.top = pos.y;
        canvas.add(this.measurementText);
        if (isParentShapeNameSameAsActiveDrawing) {
            canvas.add(this.labelText);
        }
    }

    lighten(): void {
        if (this.isInterior) {
            return;
        }

        this.measurementText.set({
            stroke: DRAWING_SHAPE_COLOR_PALETTE.NORMAL.STROKE,
            fill: DRAWING_SHAPE_COLOR_PALETTE.NORMAL.STROKE,
        });

        this.labelText.set({
            stroke: DRAWING_SHAPE_COLOR_PALETTE.NORMAL.STROKE,
            fill: DRAWING_SHAPE_COLOR_PALETTE.NORMAL.STROKE,
        });
    }

    darken(): void {
        if (this.isInterior) {
            return;
        }

        this.labelText.set({
            stroke: DRAWING_SHAPE_COLOR_PALETTE.ACTIVE.STROKE,
            fill: DRAWING_SHAPE_COLOR_PALETTE.ACTIVE.STROKE,
        });

        this.measurementText.set({
            stroke: DRAWING_SHAPE_COLOR_PALETTE.ACTIVE.STROKE,
            fill: DRAWING_SHAPE_COLOR_PALETTE.ACTIVE.STROKE,
        });
    }

    getMeasurementPosition(arrow: LineSegment, isParentShapeNameSameAsActiveDrawing: boolean): Vec2 {
        const diffVec = arrow.endPoint.subtract(arrow.startPoint, true);
        const normalVec = diffVec.rotate(Math.PI / 2, undefined, true).normalize();
        const pos = arrow.startPoint.add(arrow.endPoint, true).multiply(0.5);
        const boundRc = this.measurementText.getBoundingRect();
        const direction = getDirection(arrow);

        let sign = 1;
        switch (direction) {
            case LineDirection.TOP:
                pos.add(normalVec.multiply(OFFSET_MEASUREMENT_TEXT + boundRc.height, true));
                pos.set(pos.x - boundRc.width * 0.5, pos.y, false);
                break;
            case LineDirection.BOTTOM:
                pos.add(normalVec.multiply(OFFSET_MEASUREMENT_TEXT, true));
                pos.set(pos.x - boundRc.width * 0.5, pos.y, false);
                break;
            case LineDirection.VERTICAL_LEFT:
                pos.set(pos.x - boundRc.width - OFFSET_MEASUREMENT, pos.y - boundRc.height * 0.5, false);
                break;
            case LineDirection.VERTICAL_RIGHT:
                pos.set(
                    pos.x + OFFSET_MEASUREMENT + (isParentShapeNameSameAsActiveDrawing ? LABEL_OFFSET : 0),
                    pos.y - boundRc.height * 0.5,
                    false
                );
                break;
            case LineDirection.LEFT:
                sign = normalVec.y < 0 ? -1 : 0;
                pos.set(pos.x - boundRc.width, pos.y + sign * boundRc.height, false);
                break;
            default:
                sign = normalVec.y < 0 ? -1 : 0;
                pos.set(pos.x, pos.y + sign * boundRc.height, false);
                break;
        }

        //Pos refinement
        if (sign === 0 && (direction === LineDirection.LEFT || direction === LineDirection.RIGHT)) {
            pos.set(pos.x, pos.y + OFFSET_MEASUREMENT, false);
        }
        return pos;
    }
}

/**
 * Return just the product ID of the corner radius product associated with a given radius size
 * @param radius the radius of the corner
 * @param cornerRadii the corner radius list
 * @see getCornerRadiusFromSize
 */
export const getCornerRadiusProductIdFromSize = (radius: number, cornerRadii: CornerRadiusModel[]): number => {
    return getCornerRadiusFromSize(radius, cornerRadii)?.productId ?? FREE_CORNER_RADIUS.ID;
};

/**
 * Return the product record of the corner radius product associated with a given radius size
 * @param radius the radius of the corner
 * @param cornerRadii the corner radius list
 * @see getCornerRadiusProductIdFromSize
 */
export const getCornerRadiusFromSize = (
    radius: number,
    cornerRadii: CornerRadiusModel[]
): CornerRadiusModel | undefined => {
    return cornerRadii.find((cornerRadius) => {
        const min = getMinFromRange(cornerRadius);
        const max = getMaxFromRange(cornerRadius);
        // the first check is just to be safe if we get a zero value (should never happen) and have a zero minimum (default radius)
        return (!radius && !min) || (radius > min && radius <= max);
    });
};

export class EditableCornerLabel extends EditableLabel {
    constructor(
        parentShape: DrawingShape,
        rootCornerInfo: CornerInfo,
        label: string,
        onEdit: (editedCorner: CornerInfo, shape: DrawingShape, size: number) => void,
        canvas: fabric.Canvas,
        interiorColor = BLACK,
        index: number,
        _cornerRadii: CornerRadiusModel[]
    ) {
        super(parentShape, canvas, interiorColor, label, rootCornerInfo);

        if (!rootCornerInfo.size) {
            console.error('Tried to init editableLabel with undefined radius', rootCornerInfo);
        }
        this.measurementText.fontSize = 2.5;
        this.labelText.fontSize = 2.5;

        //after edit, attempt to get a numeric radius and validate against constraints, then call the onEdit handler to apply it
        this.measurementText.on('editing:exited', () => {
            const attemptedRadius = this.measurementText.text ? parseFloat(this.measurementText.text) : NaN;
            const radius = isNaN(attemptedRadius)
                ? rootCornerInfo.size
                : enforceRadiusConstraints(rootCornerInfo, attemptedRadius);
            onEdit(rootCornerInfo, parentShape, radius ?? rootCornerInfo.size ?? DEFAULT_CORNER_RADIUS);
        });

        const boundRcLabel = this.labelText.getBoundingRect();
        const boundRcMeasurement = this.measurementText.getBoundingRect();

        const outlinePoints = getOutlinePointsFromShape(parentShape);
        const beforeIndex = index - 1 < 0 ? outlinePoints.length - 1 : index - 1;
        const afterIndex = index + 1 > outlinePoints.length - 1 ? 0 : index + 1;

        const cornerPos = getCornerLabelPosition(
            outlinePoints[beforeIndex],
            outlinePoints[index],
            outlinePoints[afterIndex],
            boundRcLabel.width + boundRcMeasurement.width,
            boundRcLabel.height
        );

        this.measurementText.left = cornerPos.x;
        this.measurementText.top = cornerPos.y;
        this.labelText.left = cornerPos.x - LABEL_OFFSET_CORNER_SIGN;
        this.labelText.top = cornerPos.y;
        rootCornerInfo.labelText = this.labelText;
        rootCornerInfo.labelMeasure = this.measurementText;
        canvas.add(this.measurementText);
        canvas.add(this.labelText);
    }
}
